import { motion } from "framer-motion";
import { Package as PackageIcon, Edit, Trash2, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { Package } from "./types";

interface PackagesCardProps {
	packages: Package[];
	newPackage: {
		name: string;
		description: string;
		price: number;
		discount_percentage: number;
	};
	onNewPackageChange: (field: string, value: string | number) => void;
	onAddPackage: () => void;
	onEditPackage: (pkg: Package) => void;
	onDeletePackage: (id: string, name: string) => void;
	formatPrice: (value: string) => string;
}

export default function PackagesCard({
	packages,
	newPackage,
	onNewPackageChange,
	onAddPackage,
	onEditPackage,
	onDeletePackage,
	formatPrice,
}: PackagesCardProps) {
	const handlePriceChange = (value: string) => {
		const formatted = formatPrice(value);
		const numericValue = parseFloat(formatted.replace(/[^0-9.]/g, "")) || 0;
		onNewPackageChange("price", numericValue);
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6, delay: 0.6 }}
			className="mt-8"
		>
			<Card className="glass-card border-0 shadow-xl">
				<CardHeader className="text-center pb-6">
					<div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
						<PackageIcon className="h-8 w-8 text-newTheme-orange" />
					</div>
					<CardTitle className="text-2xl font-bold text-gray-900 font-karla">
						Packages &amp; Services
					</CardTitle>
					<p className="text-black font-karla mt-2 max-w-2xl mx-auto">
						Create service packages that outline what you offer and your pricing
						structure.
					</p>
				</CardHeader>
				<CardContent className="space-y-8">
					{/* Add New Package */}
					<div className="bg-primary-50 rounded-lg p-4">
						<h3 className="font-semibold text-gray-900 font-karla mb-4">
							Add New Package
						</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<Label
									htmlFor="packageName"
									className="text-sm font-medium text-gray-700 font-karla"
								>
									Package Name
								</Label>
								<Input
									id="packageName"
									placeholder="e.g., Birth Support Package"
									value={newPackage.name}
									onChange={(e) => onNewPackageChange("name", e.target.value)}
									className="mt-1"
								/>
							</div>
							<div className="grid grid-cols-2 gap-2">
								<div>
									<Label
										htmlFor="packagePrice"
										className="text-sm font-medium text-gray-700 font-karla"
									>
										Price ($)
									</Label>
									<Input
										type="number"
										id="packagePrice"
										placeholder="0.00"
										value={newPackage.price || ""}
										onChange={(e) => handlePriceChange(e.target.value)}
										className="mt-1"
									/>
								</div>
								<div>
									<Label
										htmlFor="packageDiscount"
										className="text-sm font-medium text-gray-700 font-karla"
									>
										Discount (%)
									</Label>
									<Input
										id="packageDiscount"
										type="number"
										placeholder="0"
										value={newPackage.discount_percentage || ""}
										onChange={(e) =>
											onNewPackageChange(
												"discount_percentage",
												parseInt(e.target.value) || 0,
											)
										}
										className="mt-1"
									/>
								</div>
							</div>
						</div>
						<div className="mt-4">
							<Label
								htmlFor="packageDescription"
								className="text-sm font-medium text-gray-700 font-karla"
							>
								Description
							</Label>
							<Textarea
								id="packageDescription"
								placeholder="Describe what's included in this package..."
								value={newPackage.description}
								onChange={(e) =>
									onNewPackageChange("description", e.target.value)
								}
								rows={3}
								className="mt-1"
							/>
						</div>
						<Button
							onClick={onAddPackage}
							className="mt-4 bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow"
							disabled={!newPackage.name.trim()}
						>
							<Plus className="h-4 w-4 mr-2" />
							Add Package
						</Button>
					</div>

					{/* Existing Packages */}
					<div>
						<h4 className="font-semibold text-newTheme-grey font-karla mb-4">
							Your Packages
						</h4>
						{packages.length > 0 ? (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								{packages.map((pkg) => (
									<Card
										key={pkg.id}
										className="border border-gray-200 hover:shadow-md transition-shadow"
									>
										<CardContent className="p-6">
											<div className="flex justify-between items-start mb-4">
												<div className="flex-1 mr-4">
													<h5 className="font-semibold text-newTheme-grey font-karla">
														{pkg.name}
													</h5>
													<p className="text-gray-600 font-karla text-sm mt-1">
														{pkg.description}
													</p>
												</div>
												<div className="text-right">
													<p className="text-lg font-bold text-newTheme-orange font-karla">
														${pkg.price.toFixed(2)}
													</p>
													{pkg.discount_percentage > 0 && (
														<p className="text-sm text-newTheme-spruce font-karla">
															{pkg.discount_percentage}% off
														</p>
													)}
												</div>
											</div>
											<div className="flex space-x-2">
												<Button
													variant="outline"
													size="sm"
													onClick={() => onEditPackage(pkg)}
													className="border-newTheme-lightGrey text-black hover:bg-gray-50 shadow-btn-shadow hover:text-black flex-1"
												>
													<Edit className="h-4 w-4 mr-1" />
													Edit
												</Button>
												<Button
													variant="outline"
													size="sm"
													onClick={() => onDeletePackage(pkg.id, pkg.name)}
													className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-600 shadow-btn-shadow"
												>
													<Trash2 className="h-4 w-4 mr-1" />
													Delete
												</Button>
											</div>
										</CardContent>
									</Card>
								))}
							</div>
						) : (
							<div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
								<PackageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
								<p className="text-gray-500 font-karla text-lg">
									No packages created yet
								</p>
								<p className="text-gray-400 font-karla text-sm mt-2">
									Add your first package to start offering services to families
								</p>
							</div>
						)}
					</div>
				</CardContent>
			</Card>
		</motion.div>
	);
}
